import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, ArrowRight, Check, Info } from 'lucide-react';
import { rbacApi, trainingApi } from '../../services/api';
import { feature } from '../../services/telemetry';

/**
 * Guided setup for an API token, built around SCOPE x ROLE.
 *
 * The configuration that prompted this: a token was given three projects and
 * behaved as read-only. Two separate causes, neither visible anywhere —
 * `projectIds` was ignored on standard tokens, and `accountType` also decided
 * the role, so `standard` presented as `project_resource` and was refused by 95
 * role-gated routes. The only way out was super_admin, which granted
 * everything.
 *
 * So the wizard asks the two questions separately, in the model's own terms:
 *   SCOPE (tiers 6-10)  — WHERE the token may act
 *   ROLE  (tiers 1-5)   — WHAT it may do there
 * and previews the result from the server's own resolver before saving.
 */

type Props = {
  projects: any[];
  projectGroups: any[];
  users: any[];
  organizations: any[];
  onClose: () => void;
  onCreated: (token: any) => void;
};

const STEPS = ['Purpose', 'Scope', 'Role', 'Capability', 'Review'] as const;

const PURPOSES = [
  {
    id: 'integration', label: 'Application integration',
    hint: 'A service that reads and files work in specific projects.',
    roleLevel: 3,
    permissions: ['workitems:read', 'workitems:create', 'workitems:update',
                  'activities:read', 'activities:create', 'projects:read',
                  'links:read', 'links:create', 'sprints:read'],
    allowedTypes: ['epic', 'feature', 'story', 'task', 'bug', 'idea'],
  },
  {
    id: 'intake', label: 'Write-only intake',
    hint: 'A public form. One route, one type, nothing readable.',
    roleLevel: 3,
    permissions: ['leads:create'],
    allowedTypes: ['lead'],
    allowedRoutes: ['POST /api/leads'],
  },
  {
    id: 'reporting', label: 'Read-only reporting',
    hint: 'Dashboards and roll-ups. Cannot change anything.',
    roleLevel: 1,
    permissions: ['workitems:read', 'projects:read', 'activities:read', 'links:read'],
    allowedTypes: [],
  },
  {
    id: 'platform', label: 'Platform automation',
    hint: 'Manages projects, releases and sprints — not just work items.',
    roleLevel: 4,
    permissions: ['workitems:read', 'workitems:create', 'workitems:update',
                  'activities:read', 'activities:create', 'projects:read',
                  'links:read', 'links:create', 'releases:read', 'releases:write',
                  'sprints:read', 'sprints:write'],
    allowedTypes: ['epic', 'feature', 'story', 'task', 'bug', 'security', 'idea'],
  },
];

export default function TokenWizard({ projects, projectGroups, users, organizations, onClose, onCreated }: Props) {
  const [step, setStep] = useState(0);
  const [vocab, setVocab] = useState<any>(null);
  const [preview, setPreview] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState<any>({
    name: '', description: '', ownerUserId: '', orgId: '',
    purpose: 'integration',
    scopeMode: 'projects' as 'projects' | 'group' | 'org',
    projectIds: [] as string[],
    groupId: '', scopeOrgId: '',
    roleLevel: 3,
    permissions: PURPOSES[0].permissions,
    allowedTypes: PURPOSES[0].allowedTypes,
    allowedRoutes: [] as string[],
  });

  useEffect(() => { rbacApi.tokenVocabulary().then(setVocab).catch(() => {}); }, []);

  // Training programme: one more TYPE of token (EP-26 FE-16). The programme decides its
  // reach; it acts as its owner, who must be in the programme's training groups.
  const [programmes, setProgrammes] = useState<any[]>([]);
  useEffect(() => { trainingApi.programmes().then(setProgrammes).catch(() => {}); }, []);
  const training = form.purpose === 'training';

  // The projects the chosen scope actually resolves to. A group is expanded
  // here rather than stored as a group reference, because the token floor is a
  // project list — showing a group and storing something else is how the
  // previous model misled people.
  const resolvedProjectIds: string[] = useMemo(() => {
    if (form.scopeMode === 'group') {
      return projectGroups.find((g: any) => g._id === form.groupId)?.projectIds || [];
    }
    if (form.scopeMode === 'org') {
      return projects.filter((p: any) => p.organizationId === form.scopeOrgId).map((p: any) => p._id);
    }
    return form.projectIds;
  }, [form, projects, projectGroups]);

  const payload = useMemo(() => ({
    name: form.name.trim(),
    description: form.description,
    ownerUserId: form.ownerUserId,
    orgId: form.orgId,
    accountType: form.roleLevel >= 5 ? 'super_admin' : 'standard',
    roleLevel: form.roleLevel,
    projectIds: resolvedProjectIds,
    permissions: form.permissions,
    allowedTypes: form.allowedTypes,
    allowedRoutes: form.allowedRoutes,
    active: true,
  }), [form, resolvedProjectIds]);

  useEffect(() => {
    if (step < 4) return;
    rbacApi.tokenPreview(payload).then(setPreview).catch(() => setPreview(null));
  }, [step, payload]);

  const applyPurpose = (id: string) => {
    const p = PURPOSES.find((x) => x.id === id)!;
    setForm((f: any) => ({
      ...f, purpose: id, roleLevel: p.roleLevel, permissions: p.permissions,
      allowedTypes: p.allowedTypes, allowedRoutes: p.allowedRoutes || [],
    }));
  };

  const canAdvance = () => {
    if (step === 0 && training) return form.name.trim() && form.ownerUserId && form.programId;
    if (step === 0) return form.name.trim() && form.ownerUserId;
    if (step === 1) return resolvedProjectIds.length > 0;
    return true;
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const created = await rbacApi.createTokenViaWizard(training
        ? { kind: 'training', programId: form.programId, name: form.name.trim(), description: form.description,
            ownerUserId: form.ownerUserId, expiresInDays: Number(form.expiresInDays) || 90 }
        : payload);
      // The wizard posts to /api-tokens like the plain form, so it is counted here.
      feature('admin.token-wizard');
      onCreated(created);
    } catch (e: any) {
      setError(e.message || 'Failed to create token');
    } finally { setSaving(false); }
  };

  const input = 'px-2.5 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md w-full';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-white dark:bg-gray-800 shadow-xl">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">New API token</div>
          <div className="mt-2 flex items-center gap-1.5">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-1.5">
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                  i === step ? 'bg-accent text-white'
                    : i < step ? 'bg-accent/15 text-accent'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-400'}`}>{s}</span>
                {i < STEPS.length - 1 && <span className="text-gray-300">·</span>}
              </div>
            ))}
          </div>
        </div>

        <div className="p-5 space-y-4">
          {step === 0 && (
            <>
              <Field label="Name">
                <input className={input} value={form.name} autoFocus
                       onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="Owner">
                <select className={input} value={form.ownerUserId}
                        onChange={(e) => setForm({ ...form, ownerUserId: e.target.value })}>
                  <option value="">— pick an owner —</option>
                  {users.map((u: any) => <option key={u._id} value={u._id}>{u.name || u.email}</option>)}
                </select>
              </Field>
              <Field label="What is this token for?">
                <div className="grid sm:grid-cols-2 gap-2">
                  <button onClick={() => setForm({ ...form, purpose: 'training' })}
                          className={`text-left p-2.5 rounded-lg border ${
                            training ? 'border-accent bg-accent/5' : 'border-gray-200 dark:border-gray-700'}`}>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Training programme</div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      VS Code or an agent working on one training programme. Acts as the owner, view and operate only; expires.</div>
                  </button>
                  {PURPOSES.map((p) => (
                    <button key={p.id} onClick={() => applyPurpose(p.id)}
                            className={`text-left p-2.5 rounded-lg border ${
                              form.purpose === p.id
                                ? 'border-accent bg-accent/5'
                                : 'border-gray-200 dark:border-gray-700'}`}>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{p.label}</div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{p.hint}</div>
                    </button>
                  ))}
                </div>
              </Field>
            </>
          )}

          {step === 0 && training && (
            <>
              <Field label="Training programme">
                <select className={input} value={form.programId || ''}
                        onChange={(e) => setForm({ ...form, programId: e.target.value })}>
                  <option value="">— pick a programme —</option>
                  {programmes.map((p: any) => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </Field>
              <Field label="Expires in (days, at most 180)">
                <input className={input} type="number" min={1} max={180} value={form.expiresInDays ?? 90}
                       onChange={(e) => setForm({ ...form, expiresInDays: e.target.value })} />
              </Field>
              <Note>
                The programme decides everything this token reaches: its training pages and its training project's work
                items, comments and links. It acts as the owner for viewing and operating work, never approves, and stops
                working when it expires or the owner leaves the programme's training groups. Name it after the machine or
                session it is for. The token is shown once.
              </Note>
            </>
          )}

          {step === 1 && (
            <>
              <Note>
                SCOPE is <em>where</em> the token may act — tiers 6–10 of the platform
                model. It is independent of what the token may do there.
              </Note>
              <Field label="Scope">
                <div className="flex gap-2 mb-2">
                  {([['projects', 'Named projects'], ['group', 'Project group'], ['org', 'Organization']] as const).map(([id, label]) => (
                    <button key={id} onClick={() => setForm({ ...form, scopeMode: id })}
                            className={`text-xs px-2.5 py-1.5 rounded-md ${
                              form.scopeMode === id ? 'bg-accent text-white'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                      {label}
                    </button>
                  ))}
                </div>

                {form.scopeMode === 'group' && (
                  <select className={input} value={form.groupId}
                          onChange={(e) => setForm({ ...form, groupId: e.target.value })}>
                    <option value="">— pick a group —</option>
                    {projectGroups.map((g: any) => (
                      <option key={g._id} value={g._id}>{g.name} ({(g.projectIds || []).length})</option>
                    ))}
                  </select>
                )}

                {form.scopeMode === 'org' && (
                  <select className={input} value={form.scopeOrgId}
                          onChange={(e) => setForm({ ...form, scopeOrgId: e.target.value })}>
                    <option value="">— pick an organization —</option>
                    {organizations.map((o: any) => <option key={o._id} value={o._id}>{o.name}</option>)}
                  </select>
                )}

                {form.scopeMode === 'projects' && (
                  <div className="max-h-56 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-2">
                    {projects.map((p: any) => (
                      <label key={p._id} className="flex items-center gap-2 py-0.5 text-sm text-gray-700 dark:text-gray-300">
                        <input type="checkbox" checked={form.projectIds.includes(p._id)}
                               onChange={(e) => setForm({
                                 ...form,
                                 projectIds: e.target.checked
                                   ? [...form.projectIds, p._id]
                                   : form.projectIds.filter((x: string) => x !== p._id),
                               })} />
                        {p.name}
                      </label>
                    ))}
                  </div>
                )}
              </Field>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Resolves to <span className="font-medium">{resolvedProjectIds.length}</span> project(s).
                {resolvedProjectIds.length === 0 && (
                  <span className="text-amber-600 dark:text-amber-400">
                    {' '}A token with no projects is UNRESTRICTED at the floor, not limited to nothing.
                  </span>
                )}
              </div>
            </>
          )}

          {step === 2 && vocab && (
            <>
              <Note>
                ROLE is <em>what</em> the token may do — tiers 1–5. This is the setting
                that decides whether project and release management routes accept it,
                and it used to be welded to the account type.
              </Note>
              <div className="grid gap-2">
                {vocab.roleLevels.map((r: any) => {
                  const refused = Object.entries(vocab.roleGatedRouteCounts || {})
                    .filter(([role]) => rank(role) > rank(r.legacyRole))
                    .reduce((n, [, c]) => n + (c as number), 0);
                  return (
                    <button key={r.level} onClick={() => setForm({ ...form, roleLevel: r.level })}
                            className={`text-left p-2.5 rounded-lg border ${
                              form.roleLevel === r.level
                                ? 'border-accent bg-accent/5'
                                : 'border-gray-200 dark:border-gray-700'}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                          level {r.level}
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.label}</span>
                      </div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                        {r.description}
                        {refused > 0 && ` Refused by roughly ${refused} role-gated route(s).`}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <Field label="Permissions">
                <div className="grid sm:grid-cols-2 gap-1">
                  {(vocab?.actionMinLevel ? Object.keys(PERM_LABELS) : Object.keys(PERM_LABELS)).map((id) => (
                    <label key={id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input type="checkbox" checked={form.permissions.includes(id)}
                             onChange={(e) => setForm({
                               ...form,
                               permissions: e.target.checked
                                 ? [...form.permissions, id]
                                 : form.permissions.filter((x: string) => x !== id),
                             })} />
                      {PERM_LABELS[id]}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Work item types it may write (empty = all)">
                <div className="flex flex-wrap gap-1.5">
                  {ALL_TYPES.map((t) => (
                    <button key={t} onClick={() => setForm({
                      ...form,
                      allowedTypes: form.allowedTypes.includes(t)
                        ? form.allowedTypes.filter((x: string) => x !== t)
                        : [...form.allowedTypes, t],
                    })}
                      className={`text-[11px] px-2 py-1 rounded ${
                        form.allowedTypes.includes(t)
                          ? 'bg-accent text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </Field>
              {form.allowedRoutes.length > 0 && (
                <Field label="Route allow-list">
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {form.allowedRoutes.join(', ')} — deny-by-default: nothing else is callable.
                  </div>
                </Field>
              )}
            </>
          )}

          {step === 4 && (
            <>
              {preview && !preview.valid && (
                <div className="flex gap-2 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                  <AlertTriangle size={15} className="shrink-0 mt-0.5" /> {preview.error}
                </div>
              )}
              {preview?.valid && (
                <>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{preview.summary}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Presents to route checks as <span className="font-medium">{preview.legacyRole}</span>,
                    reaching {preview.projectNames?.length || 0} project(s).
                  </div>
                  {preview.notes?.map((n: string, i: number) => (
                    <div key={i} className="flex gap-2 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-md px-2.5 py-2">
                      <AlertTriangle size={13} className="shrink-0 mt-0.5" /> {n}
                    </div>
                  ))}
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Projects: {(preview.projectNames || []).join(', ') || '—'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    RBAC permissions: {(preview.mappedPermissions || []).join(', ') || '—'}
                  </div>
                </>
              )}
              {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <button onClick={onClose} className="text-sm text-gray-500 dark:text-gray-400">Cancel</button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button onClick={() => setStep(step - 1)}
                      className="text-sm flex items-center gap-1 px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                <ArrowLeft size={14} /> Back
              </button>
            )}
            {training ? (
              <button onClick={save} disabled={saving || !canAdvance()}
                      className="text-sm flex items-center gap-1 px-3 py-1.5 rounded-md bg-accent text-white disabled:opacity-40">
                <Check size={14} /> {saving ? 'Creating…' : 'Create training token'}
              </button>
            ) : step < STEPS.length - 1 ? (
              <button onClick={() => setStep(step + 1)} disabled={!canAdvance()}
                      className="text-sm flex items-center gap-1 px-3 py-1.5 rounded-md bg-accent text-white disabled:opacity-40">
                Next <ArrowRight size={14} />
              </button>
            ) : (
              <button onClick={save} disabled={saving || !preview?.valid}
                      className="text-sm flex items-center gap-1 px-3 py-1.5 rounded-md bg-accent text-white disabled:opacity-40">
                <Check size={14} /> {saving ? 'Creating…' : 'Create token'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const PERM_LABELS: Record<string, string> = {
  'workitems:read': 'Read work items',
  'workitems:create': 'Create work items',
  'workitems:update': 'Update work items',
  'activities:read': 'Read activities',
  'activities:create': 'Create activities',
  'projects:read': 'Read projects',
  'links:read': 'Read commit/PR links',
  'links:create': 'Create commit/PR links',
  'releases:read': 'Read releases',
  'releases:write': 'Manage releases',
  'sprints:read': 'Read PIs and sprints',
  'sprints:write': 'Plan PIs and sprints',
  'rbac:check': "Check a person's access (services)",
  'comments:create': 'Comment on work items',
  'leads:create': 'Submit leads',
  'approvals:evidence': 'Raise and follow Research evidence approvals (services)',
  'usage:record': 'Send monthly usage records to the CRM (services)',
};

const ALL_TYPES = ['epic', 'feature', 'story', 'task', 'bug', 'security', 'idea', 'lead', 'opportunity'];

const RANKS: Record<string, number> = {
  read_only: 1, project_resource: 2, project_admin: 3, super_admin: 4,
};
function rank(role: string) { return RANKS[role] || 0; }

function Field({ label, children }: any) {
  return (
    <div>
      <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">{label}</div>
      {children}
    </div>
  );
}

function Note({ children }: any) {
  return (
    <div className="flex gap-2 text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/40 rounded-md px-2.5 py-2">
      <Info size={13} className="shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}
